import React, { useEffect } from 'react';
import PropTypes from "prop-types";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Collapse } from '../../Components/ui';

import navdata from "../LayoutMenuData";
import { withTranslation } from "react-i18next";
import { SITE_URL } from '@/api';
import { cn } from '@/lib/utils';

const VerticalLayout = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const navData = navdata().props.children;
    const currentPath = location.pathname;
    const currentFullPath = SITE_URL + location.pathname;

    const isPlaceholderLink = (link) => {
        if (!link) return true;
        return link === "/#" || link === "/#/" || link === "#";
    };

    const isActiveLink = (link) => {
        if (isPlaceholderLink(link)) return false;
        return link === currentPath || link === currentFullPath;
    };

    const hasActiveDescendant = (items) => {
        if (!items || !items.length) return false;
        return items.some((entry) => {
            if (entry.isHeader) return false;
            if (isActiveLink(entry.link)) return true;
            return hasActiveDescendant(entry.subItems) || hasActiveDescendant(entry.childItems);
        });
    };

    const isSidebarCollapsed = () => {
        const sidebarSize = document.documentElement.getAttribute("data-sidebar-size");
        return sidebarSize === "sm" || sidebarSize === "sm-hover" || sidebarSize === "sm-hover-active";
    };

    const expandSidebar = () => {
        document.documentElement.setAttribute("data-sidebar-size", "lg");
        document.body.classList.remove("vertical-sidebar-enable");
    };

    const handleMenuInteraction = (
        e: React.MouseEvent<any>,
        action?: (event: React.MouseEvent<any>) => void,
        link?: string,
        target?: string
    ) => {
        if (!isSidebarCollapsed()) {
            if (typeof action === "function") action(e);
            return;
        }

        e.preventDefault();
        expandSidebar();

        if (typeof action === "function") {
            action(e);
            return;
        }

        if (!isPlaceholderLink(link)) {
            if (target === "_blank") {
                window.open(link, "_blank", "noopener,noreferrer");
                return;
            }
            navigate(link);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (props.layoutType === "vertical") {
            const openParents = (items) => {
                if (!items || !items.length) return false;
                let hasActive = false;
                items.forEach((entry) => {
                    if (entry.isHeader) return;
                    const childActive = openParents(entry.subItems) || openParents(entry.childItems);
                    const selfActive = isActiveLink(entry.link);
                    if (childActive && typeof entry.click === "function" && entry.stateVariables === false) {
                        entry.click({ preventDefault: () => {} });
                    }
                    if (selfActive || childActive) {
                        hasActive = true;
                    }
                });
                return hasActive;
            };

            openParents(navData);
        }
    }, [currentPath, props.layoutType]);

    const topLevelLinkClasses = "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800 hover:text-white";
    const topLevelActiveClasses = "bg-slate-800 text-white";
    const subLinkClasses = "flex items-center rounded-md px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-white";
    const subActiveClasses = "bg-slate-800/70 text-white";

    return (
        <React.Fragment>
            {(navData || []).map((item, key) => {
                const isItemActive = isActiveLink(item.link);
                const hasItemActiveChild = hasActiveDescendant(item.subItems) || hasActiveDescendant(item.childItems);
                const isItemExpanded = Boolean(item.stateVariables);
                return (
                    <React.Fragment key={key}>
                        {item['isHeader'] ?
                            <li className="menu-title px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                <span data-key="t-menu">{props.t(item.label)}</span>
                            </li>
                            : (
                                (item.subItems ? (
                                    <li className="nav-item space-y-1">
                                        <Link
                                            onClick={(e) => handleMenuInteraction(e, item.click, item.link)}
                                            className={cn(
                                                "nav-link menu-link",
                                                topLevelLinkClasses,
                                                (isItemActive || hasItemActiveChild) && topLevelActiveClasses
                                            )}
                                            to={item.link ? item.link : "/#"}
                                            aria-expanded={isItemExpanded}
                                        >
                                            <i className={item.icon}></i> <span data-key="t-apps">{props.t(item.label)}</span>
                                        </Link>
                                        <Collapse
                                            className="menu-dropdown mt-1 pl-3"
                                            isOpen={isItemExpanded}
                                            id="sidebarApps">
                                            <ul className="flex flex-col gap-1">
                                                {item.subItems && ((item.subItems || []).map((subItem, key) => (
                                                    <React.Fragment key={key}>
                                                        {!subItem.isChildItem ? (
                                                            <li className="nav-item">
                                                                <Link
                                                                    to={subItem.link ? subItem.link : "/#"}
                                                                    onClick={(e) => handleMenuInteraction(e, undefined, subItem.link)}
                                                                    className={cn(
                                                                        "nav-link",
                                                                        subLinkClasses,
                                                                        isActiveLink(subItem.link) && subActiveClasses
                                                                    )}
                                                                >
                                                                    {props.t(subItem.label)}
                                                                    {subItem.badgeName ?
                                                                        <Badge
                                                                            color={subItem.badgeColor}
                                                                            pill
                                                                            className="badge ml-auto text-[11px]"
                                                                        >
                                                                            {subItem.badgeName}
                                                                        </Badge>
                                                                        : null}
                                                                </Link>
                                                            </li>
                                                        ) : (
                                                            <li className="nav-item space-y-1">
                                                                <Link
                                                                    onClick={(e) => handleMenuInteraction(e, subItem.click, subItem.link)}
                                                                    className={cn(
                                                                        "nav-link",
                                                                        subLinkClasses,
                                                                        (isActiveLink(subItem.link) || hasActiveDescendant(subItem.childItems)) && subActiveClasses
                                                                    )}
                                                                    to="/#"
                                                                    aria-expanded={Boolean(subItem.stateVariables)}
                                                                > {props.t(subItem.label)}
                                                                </Link>
                                                                <Collapse className="menu-dropdown mt-1 pl-3" isOpen={Boolean(subItem.stateVariables)} id="sidebarEcommerce">
                                                                    <ul className="flex flex-col gap-1">
                                                                        {subItem.childItems && (
                                                                            (subItem.childItems || []).map((childItem, key) => (
                                                                                <React.Fragment key={key}>
                                                                                    {!childItem.childItems ?
                                                                                        <li className="nav-item">
                                                                                            <Link
                                                                                                to={childItem.link ? childItem.link : "/#"}
                                                                                                onClick={(e) => handleMenuInteraction(e, undefined, childItem.link)}
                                                                                                className={cn(
                                                                                                    "nav-link",
                                                                                                    subLinkClasses,
                                                                                                    isActiveLink(childItem.link) && subActiveClasses
                                                                                                )}>
                                                                                                {props.t(childItem.label)}
                                                                                            </Link>
                                                                                        </li>
                                                                                        : <li className="nav-item space-y-1">
                                                                                            <Link
                                                                                                to="/#"
                                                                                                className={cn(
                                                                                                    "nav-link",
                                                                                                    subLinkClasses,
                                                                                                    (isActiveLink(childItem.link) || hasActiveDescendant(childItem.childItems)) && subActiveClasses
                                                                                                )}
                                                                                                onClick={(e) => handleMenuInteraction(e, childItem.click, childItem.link)}
                                                                                                aria-expanded={Boolean(childItem.stateVariables)}
                                                                                            >
                                                                                                {props.t(childItem.label)}
                                                                                            </Link>
                                                                                            <Collapse className="menu-dropdown mt-1 pl-3" isOpen={Boolean(childItem.stateVariables)} id="sidebaremailTemplates">
                                                                                                <ul className="flex flex-col gap-1">
                                                                                                    {childItem.childItems.map((subChildItem, key) => (
                                                                                                        <li className="nav-item" key={key}>
                                                                                                            <Link
                                                                                                                to={subChildItem.link}
                                                                                                                onClick={(e) => handleMenuInteraction(e, undefined, subChildItem.link)}
                                                                                                                className={cn(
                                                                                                                    "nav-link",
                                                                                                                    subLinkClasses,
                                                                                                                    isActiveLink(subChildItem.link) && subActiveClasses
                                                                                                                )}
                                                                                                                data-key="t-basic-action"
                                                                                                            >
                                                                                                                {props.t(subChildItem.label)}
                                                                                                            </Link>
                                                                                                        </li>
                                                                                                    ))}
                                                                                                </ul>
                                                                                            </Collapse>
                                                                                        </li>
                                                                                    }
                                                                                </React.Fragment>
                                                                            ))
                                                                        )}
                                                                    </ul>
                                                                </Collapse>
                                                            </li>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                                )}
                                            </ul>

                                        </Collapse>
                                    </li>
                                ) : (
                                    <li className="nav-item">
                                        {
                                            (item.directLink) ? 
                                            <a
                                                onClick={(e) => handleMenuInteraction(e, undefined, item.directLink, item.target)}
                                                className={cn(
                                                    "nav-link menu-link",
                                                    topLevelLinkClasses,
                                                    isItemActive && topLevelActiveClasses
                                                )}
                                                href={item.directLink}
                                                target={item.target ? item.target : "_self"}
                                                rel="noopener noreferrer"
                                            >
                                                <i className={item.icon}></i> <span>{props.t(item.label)}</span>
                                            </a> : 
                                            <Link
                                                onClick={(e) => handleMenuInteraction(e, undefined, item.link)}
                                                className={cn(
                                                    "nav-link menu-link",
                                                    topLevelLinkClasses,
                                                    isItemActive && topLevelActiveClasses
                                                )}
                                                to={item.link ? item.link : "/#"}>
                                                <i className={item.icon}></i> <span>{props.t(item.label)}</span>
                                            </Link>
                                        }
                                    </li>
                                ))
                            )
                        }
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(VerticalLayout);
